import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getAuthHeaders } from "../../utils/apiHeaders";

var API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function persistSession(user, token) {
  localStorage.setItem("authToken", token);
  localStorage.setItem("authUser", JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem("authToken");
  localStorage.removeItem("authUser");
}

export var registerUser = createAsyncThunk(
  "auth/registerUser",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/register", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify(payload.data),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Registration failed",
          code: responseData.code,
          errors: responseData.data,
        });
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var loginUser = createAsyncThunk(
  "auth/loginUser",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/login", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
        }),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Login failed",
          code: responseData.code,
          email:
            responseData.data && responseData.data.email
              ? responseData.data.email
              : null,
        });
      }

      persistSession(responseData.data.user, responseData.data.token);
      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/verify-otp", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify({
          email: payload.email,
          token: payload.token,
        }),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Verification failed",
          code: responseData.code,
        });
      }

      persistSession(responseData.data.user, responseData.data.token);
      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/resend-otp", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify({ email: payload.email }),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to resend code",
          code: responseData.code,
        });
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/forgot/password", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify({ email: payload.email }),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to send reset code",
          code: responseData.code,
        });
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var forgotVerifyOtp = createAsyncThunk(
  "auth/forgotVerifyOtp",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/forgot/verify-otp", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify({
          email: payload.email,
          token: payload.token,
        }),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Verification failed",
          code: responseData.code,
        });
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(API_BASE_URL + "/forgot/reset-password", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        }),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to reset password",
          code: responseData.code,
        });
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async function (language, thunkApi) {
    try {
      await fetch(API_BASE_URL + "/logout", {
        method: "POST",
        signal: thunkApi.signal,
        headers: getAuthHeaders(language),
      });
    } catch (error) {
      // Ignore network errors on logout — we clear the local session regardless.
    }
    clearSession();
    return true;
  }
);

var storedToken = localStorage.getItem("authToken");
var storedUserRaw = localStorage.getItem("authUser");
var storedUser = null;
try {
  storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
} catch (e) {
  storedUser = null;
}

var initialState = {
  user: storedUser,
  token: storedToken,

  registerLoading: false,
  registerError: "",

  loginLoading: false,
  loginError: "",
  loginErrorCode: null,
  unverifiedEmail: null,

  verifyLoading: false,
  verifyError: "",

  resendLoading: false,
  resendError: "",

  forgotLoading: false,
  forgotError: "",

  forgotVerifyLoading: false,
  forgotVerifyError: "",

  resetLoading: false,
  resetError: "",
};

var authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    clearLoginError: function (state) {
      state.loginError = "";
      state.loginErrorCode = null;
      state.unverifiedEmail = null;
    },
  },
  extraReducers: function (builder) {
    builder
      .addCase(registerUser.pending, function (state) {
        state.registerLoading = true;
        state.registerError = "";
      })
      .addCase(registerUser.fulfilled, function (state) {
        state.registerLoading = false;
      })
      .addCase(registerUser.rejected, function (state, action) {
        state.registerLoading = false;
        state.registerError =
          (action.payload && action.payload.message) || "Registration failed";
      })

      .addCase(loginUser.pending, function (state) {
        state.loginLoading = true;
        state.loginError = "";
        state.loginErrorCode = null;
        state.unverifiedEmail = null;
      })
      .addCase(loginUser.fulfilled, function (state, action) {
        state.loginLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, function (state, action) {
        state.loginLoading = false;
        state.loginError = (action.payload && action.payload.message) || "Login failed";
        state.loginErrorCode = action.payload && action.payload.code;
        state.unverifiedEmail =
          action.payload && action.payload.code === 415
            ? action.payload.email
            : null;
      })

      .addCase(verifyOtp.pending, function (state) {
        state.verifyLoading = true;
        state.verifyError = "";
      })
      .addCase(verifyOtp.fulfilled, function (state, action) {
        state.verifyLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(verifyOtp.rejected, function (state, action) {
        state.verifyLoading = false;
        state.verifyError =
          (action.payload && action.payload.message) || "Verification failed";
      })

      .addCase(resendOtp.pending, function (state) {
        state.resendLoading = true;
        state.resendError = "";
      })
      .addCase(resendOtp.fulfilled, function (state) {
        state.resendLoading = false;
      })
      .addCase(resendOtp.rejected, function (state, action) {
        state.resendLoading = false;
        state.resendError =
          (action.payload && action.payload.message) || "Unable to resend code";
      })

      .addCase(forgotPassword.pending, function (state) {
        state.forgotLoading = true;
        state.forgotError = "";
      })
      .addCase(forgotPassword.fulfilled, function (state) {
        state.forgotLoading = false;
      })
      .addCase(forgotPassword.rejected, function (state, action) {
        state.forgotLoading = false;
        state.forgotError =
          (action.payload && action.payload.message) || "Unable to send reset code";
      })

      .addCase(forgotVerifyOtp.pending, function (state) {
        state.forgotVerifyLoading = true;
        state.forgotVerifyError = "";
      })
      .addCase(forgotVerifyOtp.fulfilled, function (state) {
        state.forgotVerifyLoading = false;
      })
      .addCase(forgotVerifyOtp.rejected, function (state, action) {
        state.forgotVerifyLoading = false;
        state.forgotVerifyError =
          (action.payload && action.payload.message) || "Verification failed";
      })

      .addCase(resetPassword.pending, function (state) {
        state.resetLoading = true;
        state.resetError = "";
      })
      .addCase(resetPassword.fulfilled, function (state) {
        state.resetLoading = false;
      })
      .addCase(resetPassword.rejected, function (state, action) {
        state.resetLoading = false;
        state.resetError =
          (action.payload && action.payload.message) || "Unable to reset password";
      })

      .addCase(logoutUser.fulfilled, function (state) {
        state.user = null;
        state.token = null;
      });
  },
});

export var clearLoginError = authSlice.actions.clearLoginError;
export default authSlice.reducer;
